// Dashboard API client (MCIP-12). One code path for fixtures and real data —
// the API decides which store backs it, the UI never knows the difference.
//
// MCIP-28: every failed call (non-2xx or network) is reported to
// self-telemetry from the ONE request core below — no per-endpoint
// instrumentation to forget. Errors carry `.status` when the server answered.
import { trackApiError } from './telemetry';

const BASE = import.meta.env.VITE_API_BASE || '';

async function request(path, { method = 'GET', body = null } = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body !== null ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    trackApiError(path, null, err.message); // network-level failure
    throw err;
  }
  const out = await res.json().catch(() => null);
  if (!res.ok) {
    const e = new Error(out?.error ? `${out.error}${out.received ? ` (${out.received})` : ''}` : `HTTP ${res.status}`);
    e.status = res.status;
    trackApiError(path, res.status, e.message);
    throw e;
  }
  return out;
}

const get = (path) => request(path);
const post = (path, body = {}) => request(path, { method: 'POST', body });

export const api = {
  health: () => get('/api/health'),
  jobs: ({ region } = {}) => {
    const q = new URLSearchParams();
    if (region) q.set('region', region);
    const qs = q.toString();
    return get(`/api/jobs${qs ? `?${qs}` : ''}`);
  },
  summary: (jobId, scope, run = null) => {
    const q = new URLSearchParams();
    if (scope) q.set('scope', scope);
    if (run) q.set('run', run);
    const qs = q.toString();
    return get(`/api/jobs/${encodeURIComponent(jobId)}/summary${qs ? `?${qs}` : ''}`);
  },
  clusters: (jobId, { view = 'human', scope = null, run = null } = {}) => {
    const q = new URLSearchParams({ view });
    if (scope) q.set('scope', scope);
    if (run) q.set('run', run);
    return get(`/api/jobs/${encodeURIComponent(jobId)}/clusters?${q}`);
  },
  // MCIP-21 — pod-overview aggregate: every pod's latest pass rate, sparkline
  // points, signals by classification, incident count and top-signal teaser in
  // ONE call (no per-job /summary fan-out). MCIP-23: region/environment slice
  // the picture server-side — us and eu are DIFFERENT cards, never one global.
  pods: ({ region = null, environment = null } = {}) => {
    const q = new URLSearchParams();
    if (region) q.set('region', region);
    if (environment) q.set('environment', environment);
    const qs = q.toString();
    return get(`/api/pods${qs ? `?${qs}` : ''}`);
  },
  // MCIP-23 — the slice matrix: per-(region × environment) summaries, the ONE
  // deliberately-mixed surface (orientation across worlds, then bifurcate).
  slices: () => get('/api/slices'),
  // MCIP-29 — suite growth (per-run case deltas) + new-case lifecycle quality.
  suiteGrowth: (jobId, { runs = 15 } = {}) =>
    get(`/api/jobs/${encodeURIComponent(jobId)}/suite-growth?runs=${runs}`),
  podSuiteGrowth: (pod, { region = null, environment = null, runs = 15 } = {}) => {
    const q = new URLSearchParams({ runs: String(runs) });
    if (region) q.set('region', region);
    if (environment) q.set('environment', environment);
    return get(`/api/pods/${encodeURIComponent(pod)}/suite-growth?${q}`);
  },
  // MCIP-26 — weekly trend series; aggregation happens store-side, the UI
  // receives one bounded payload (weeks is clamped server-side at 26).
  analytics: (jobId, { weeks = 8, scope = null } = {}) => {
    const q = new URLSearchParams({ weeks: String(weeks) });
    if (scope) q.set('scope', scope);
    return get(`/api/jobs/${encodeURIComponent(jobId)}/analytics?${q}`);
  },
  cluster: (clusterId) => get(`/api/clusters/${encodeURIComponent(clusterId)}`),
  // Cheap status probe for the async-poll contract: { available, cached, running, error }.
  aiStatus: (clusterId) => get(`/api/clusters/${encodeURIComponent(clusterId)}/ai-analysis`),
  // 200 → the (cached) result; 202 → { running: true }, the run continues server-side.
  aiAnalysis: (clusterId, { force = false } = {}) =>
    post(`/api/clusters/${encodeURIComponent(clusterId)}/ai-analysis${force ? '?force=1' : ''}`),
  // Category enum + human labels for UI selects (cached by the browser).
  categories: () => get('/api/meta/categories'),
  // Feedback loop: agree/correct verdicts; corrections become human labels.
  clusterFeedback: (clusterId, body) =>
    post(`/api/clusters/${encodeURIComponent(clusterId)}/feedback`, body),
  // Impact metrics: before/after pass-%, recurrence, time-to-first-action.
  impact: ({ weeks = 8, jobId = null } = {}) => {
    const q = new URLSearchParams({ weeks: String(weeks) });
    if (jobId) q.set('job', jobId);
    return get(`/api/impact?${q}`);
  },
  // MCIP-19 — active backend-incident groups (pod-wise, windowed, config-driven).
  // MCIP-22: ?window_hours widens/narrows per request (server clamps at 168h).
  incidents: ({ windowHours = null, region = null, environment = null } = {}) => {
    const q = new URLSearchParams();
    if (windowHours) q.set('window_hours', windowHours);
    if (region) q.set('region', region);
    if (environment) q.set('environment', environment);
    const qs = q.toString();
    return get(`/api/incidents${qs ? `?${qs}` : ''}`);
  },
  // Cheap probe for the incident-draft poll loop: { available, cached, running, error }.
  // windowHours must match the view that showed the group (a 7d-only group
  // would 404 against the server's narrower default window).
  incidentDraftStatus: (pod, { windowHours = null } = {}) => {
    const q = new URLSearchParams({ pod });
    if (windowHours) q.set('window_hours', String(windowHours));
    return get(`/api/incidents/draft?${q}`);
  },
  // 200 → the (cached) draft; 202 → { running: true }, Glean continues server-side.
  // The draft is copy-ready TEXT — nothing is ever posted to Slack from here.
  incidentDraft: (pod, { force = false, windowHours = null } = {}) =>
    post(`/api/incidents/draft${force ? '?force=1' : ''}`,
      windowHours ? { pod, window_hours: windowHours } : { pod }),
  action: (clusterId, body) =>
    post(`/api/clusters/${encodeURIComponent(clusterId)}/action`, body),
  // MCIP-28 — the tool's own health (System health card on /admin reads the
  // same endpoint; kept here so any screen can surface it later).
  telemetrySummary: ({ hours = 24 } = {}) => get(`/api/telemetry/summary?hours=${hours}`),
};
